import * as React from 'react';
import { Theme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import moment from 'moment-timezone';
import useShopFormat from 'hooks/useShopFormat';

interface OwnProps {
	onChange: (value: string) => void;
	selectedDate: string;
	startDate?: string;
	children?: any;
	outlined?: boolean;
	underlined?: boolean;
	noMinDate?: boolean;
	disabled?: boolean;
	error?: boolean;
	hideDuration?: boolean;
	hideLastTime?: boolean;
}

type Props = OwnProps & WithStyles<typeof styles>;

const TimePicker = ({ ...props }: Props) => {
	const classes = props.classes;
	const { localFormat } = useShopFormat();
	const selectedTimeFormatted = moment(props.selectedDate).format('HH:mm');

	const getTimes = () => {
		const timeArray = [];
		const minDate = moment(props.selectedDate)
			.startOf('minute')
			.isBefore(moment().startOf('minute'))
			? moment(props.selectedDate).startOf('minute')
			: moment().startOf('minute');
		for (let i = 0; i < 24; i++) {
			for (let j = 0; j < 4; j++) {
				const hours = i < 10 ? '0' + i : i + '';
				const minutes = j === 0 ? '00' : 15 * j + '';
				const timeString = hours + ':' + minutes;
				const momentTime = moment(timeString, 'HH:mm');
				const date = moment(props.selectedDate)
					.hours(parseInt(hours, 10))
					.minutes(parseInt(minutes, 10))
					.startOf('minute');
				// Check that date is after minimum date, and if setting up end date, end date is before start date
				if (
					(props.noMinDate || date.isSameOrAfter(minDate)) &&
					(!props.startDate || (props.startDate && date.isAfter(moment(props.startDate))))
				) {
					const displayTimeString =
						props.startDate && !props.hideDuration
							? showTimeWithDuration(timeString)
							: localFormat(momentTime, 'HH:mm');
					timeArray.push(
						<MenuItem key={'t' + timeString} value={timeString}>
							{displayTimeString}
						</MenuItem>,
					);
				}
			}
		}
		if (props.hideLastTime) {
			return timeArray;
		}
		const lastHour = 23;
		const lastMinute = 59;
		const lastTimeString = lastHour + ':' + lastMinute;
		const lastMomentTime = moment(lastTimeString, 'HH:mm');
		const lastDisplayTimeString =
			props.startDate && !props.hideDuration
				? showTimeWithDuration(lastTimeString)
				: localFormat(lastMomentTime, 'HH:mm');
		timeArray.push(
			<MenuItem key={'t' + lastTimeString} value={lastTimeString}>
				{lastDisplayTimeString}
			</MenuItem>,
		);
		return timeArray;
	};

	const showTimeWithDuration = (hourMinute: string) => {
		const { selectedDate, startDate } = props;
		const hourMinuteArray = hourMinute.split(':');
		const newEndDate = moment(selectedDate)
			.hours(parseInt(hourMinuteArray[0], 10))
			.minutes(parseInt(hourMinuteArray[1], 10));
		const rentalDuration = moment.duration(moment(newEndDate).diff(moment(startDate)));
		const days = Math.floor(rentalDuration.asDays());
		const hours = rentalDuration.hours();
		const minutes = rentalDuration.minutes();
		return (
			<div className={classes.optionWrapper}>
				<span className={classes.primaryOption}>
					{localFormat(moment(hourMinute, 'HH:mm'), 'HH:mm')}
				</span>
				<span className={classes.secondaryOption}>
					{days ? days + 'd ' : ''}
					{hours ? hours + 'h ' : ''}
					{!days && minutes ? minutes + 'min' : ''}
				</span>
			</div>
		);
	};

	return (
		<TextField
			select
			className={classes.timeInput}
			InputProps={{
				classes: {
					root: classes.inputRoot,
				},
				...(!props.outlined && { disableUnderline: !props.underlined }),
			}}
			SelectProps={{
				classes: {
					select: classes.select,
				},
			}}
			disabled={Boolean(props.disabled)}
			color="primary"
			error={props.error}
			variant={props.outlined ? 'outlined' : ('standard' as any)}
			value={selectedTimeFormatted}
			onChange={(e: any) => props.onChange(e.target.value as string)}
		>
			{getTimes()}
		</TextField>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		optionWrapper: {
			width: '100%',
			display: 'flex',
			justifyContent: 'space-between',
		},
		inputRoot: {
			height: '100%',
		},
		primaryOption: {
			width: 50,
		},
		secondaryOption: {
			flex: 1,
			textAlign: 'right',
			color: theme.palette.common.black,
			fontWeight: 600,
		},
		timeInput: {
			color: theme.palette.text.secondary,
			backgroundColor: theme.palette.background.paper,
			width: '100%',
			padding: 0,
			border: 0,
			margin: 1,
		},
		select: {
			padding: 10,
			paddingRight: 24,
		},
	});

export default withStyles(styles)(TimePicker);
